import { useEffect, useState } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import {
  ClockCounterClockwise,
  MagnifyingGlass,
  User,
  Robot,
  SignIn,
  UserPlus,
  PencilSimple,
  Trash,
  Key,
  ArrowsClockwise,
  Eye,
  Toolbox,
  FileText,
  CalendarCheck,
  ArrowCounterClockwise,
} from '@phosphor-icons/react';
import AdminLayout from '../components/AdminLayout';
import API_URL from '../config';
import { toast } from 'sonner';

const ACTION_META = {
  'sign_in':                 { icon: SignIn,          color: '#1d57a5', label: 'Signed in' },
  'client.create':           { icon: UserPlus,        color: '#16a34a', label: 'Client created' },
  'client.update':           { icon: PencilSimple,    color: '#1d57a5', label: 'Client updated' },
  'client.delete':           { icon: Trash,           color: '#b91c1c', label: 'Client deleted' },
  'client.toggle_active':    { icon: User,            color: '#1d57a5', label: 'Client toggled' },
  'client.reset_password':   { icon: Key,             color: '#b45309', label: 'Password reset' },
  'client.preview':          { icon: Eye,             color: '#1d57a5', label: 'Client previewed' },
  'client.sign_in':          { icon: SignIn,          color: '#16a34a', label: 'Client signed in' },
  'admin.reset_password':    { icon: Key,             color: '#b45309', label: 'Teammate password reset' },
  'matter.create':           { icon: FileText,        color: '#16a34a', label: 'Matter created' },
  'matter.update':           { icon: PencilSimple,    color: '#1d57a5', label: 'Matter updated' },
  'matter.delete':           { icon: Trash,           color: '#b91c1c', label: 'Matter deleted' },
  'matter.sync':             { icon: ArrowsClockwise, color: '#1d57a5', label: 'Matter synced' },
  'matters.sync_all':        { icon: ArrowsClockwise, color: '#1d57a5', label: 'Bulk sync' },
  'matters.scheduled_sync':  { icon: Robot,           color: '#52525B', label: 'Scheduled sync' },
  'matters.scheduled_sync_error': { icon: Robot,      color: '#b91c1c', label: 'Scheduled sync failed' },
  'matters.deadline_sync':   { icon: CalendarCheck,   color: '#006747', label: 'Deadline refresh' },
  'clients.folder_sync':     { icon: CalendarCheck,   color: '#006747', label: 'Folder refresh' },
  'clients.fix_drive_folders': { icon: CalendarCheck, color: '#006747', label: 'Folder cleanup' },
  'clients.backfill_contact_ids': { icon: CalendarCheck, color: '#006747', label: 'Contact backfill' },
  'tmtko.import':            { icon: Toolbox,         color: '#1d57a5', label: 'TM TKO import' },
};

function formatWhen(iso) {
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    return d.toLocaleString('en-US', {
      year: 'numeric', month: 'short', day: 'numeric',
      hour: 'numeric', minute: '2-digit',
      timeZone: 'America/Chicago',
    });
  } catch {
    return iso;
  }
}

export default function AdminHistory() {
  const [tab, setTab] = useState('activity'); // 'activity' | 'release'
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [releaseNotes, setReleaseNotes] = useState('');
  const [releaseLoading, setReleaseLoading] = useState(false);

  const fetchHistory = async (q = '') => {
    setLoading(true);
    try {
      const params = q ? { q } : {};
      const response = await axios.get(`${API_URL}/api/admin/history`, { params });
      setEntries(response.data.entries || []);
    } catch (e) {
      toast.error('Failed to load history');
    } finally {
      setLoading(false);
    }
  };

  const fetchReleaseNotes = async () => {
    setReleaseLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/admin/changelog`);
      setReleaseNotes(response.data.content || '');
    } catch (e) {
      toast.error('Failed to load release notes');
    } finally {
      setReleaseLoading(false);
    }
  };

  useEffect(() => { fetchHistory(); }, []);
  useEffect(() => { if (tab === 'release' && !releaseNotes) fetchReleaseNotes(); }, [tab, releaseNotes]);

  const onSearch = (e) => {
    e.preventDefault();
    fetchHistory(query.trim());
  };

  return (
    <AdminLayout>
      <div className="max-w-6xl mx-auto" data-testid="admin-history-page">
        {/* Header */}
        <div className="mb-8">
          <p className="label-uppercase mb-1" style={{ color: '#1d57a5' }}>Transparency</p>
          <h1 className="text-3xl font-bold tracking-tight" style={{ color: '#141b4d' }}>
            History
          </h1>
          <p className="text-sm mt-2 max-w-2xl" style={{ color: '#52525B' }}>
            Every admin action and scheduled job is logged here. Use this to track what teammates have done and to review product updates.
          </p>
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-1 mb-6 border-b" style={{ borderColor: '#d4cfc5' }}>
          <button
            onClick={() => setTab('activity')}
            className={`px-4 py-2 text-sm font-medium transition-colors ${tab === 'activity' ? 'border-b-2' : ''}`}
            style={tab === 'activity'
              ? { color: '#141b4d', borderColor: '#ffd100' }
              : { color: '#9a9488' }}
            data-testid="history-tab-activity"
          >
            Activity Log
          </button>
          <button
            onClick={() => setTab('release')}
            className={`px-4 py-2 text-sm font-medium transition-colors ${tab === 'release' ? 'border-b-2' : ''}`}
            style={tab === 'release'
              ? { color: '#141b4d', borderColor: '#ffd100' }
              : { color: '#9a9488' }}
            data-testid="history-tab-release"
          >
            Release Notes
          </button>
        </div>

        {tab === 'activity' && (
          <>
            {/* Search */}
            <form onSubmit={onSearch} className="mb-4 flex items-center gap-2">
              <div className="relative flex-1 max-w-md">
                <MagnifyingGlass weight="regular" className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: '#9a9488' }} />
                <input
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Filter by teammate, action, or keyword"
                  className="w-full pl-10 pr-3 py-2 text-sm border"
                  style={{ borderColor: '#d4cfc5', backgroundColor: 'white', color: '#101820' }}
                  data-testid="history-search-input"
                />
              </div>
              <button type="submit" className="btn-primary text-sm px-4 py-2" data-testid="history-search-btn">
                Filter
              </button>
              {query && (
                <button
                  type="button"
                  onClick={() => { setQuery(''); fetchHistory(''); }}
                  className="text-sm hover:underline"
                  style={{ color: '#9a9488' }}
                  data-testid="history-clear-btn"
                >
                  Clear
                </button>
              )}
            </form>

            {/* Log */}
            {loading ? (
              <div className="card-swiss text-center py-10" style={{ color: '#9a9488' }}>Loading activity…</div>
            ) : entries.length === 0 ? (
              <div className="card-swiss text-center py-10" style={{ color: '#9a9488' }}>
                <ClockCounterClockwise weight="duotone" className="w-10 h-10 mx-auto mb-2" />
                No activity to show yet.
              </div>
            ) : (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="card-swiss p-0 overflow-hidden"
              >
                <div className="grid text-xs uppercase tracking-wider font-semibold px-5 py-3 border-b" style={{ color: '#9a9488', backgroundColor: '#f2e9db', borderColor: '#d4cfc5', gridTemplateColumns: '170px 200px 180px 1fr' }}>
                  <div>When <span className="normal-case font-normal" style={{ color: '#b8b1a3' }}>(CT)</span></div>
                  <div>Who</div>
                  <div>Action</div>
                  <div>Details</div>
                </div>
                <div className="divide-y" style={{ borderColor: '#e7e2d6' }}>
                  {entries.map((entry, idx) => {
                    const meta = ACTION_META[entry.action_type] || { icon: ClockCounterClockwise, color: '#52525B', label: entry.action_type };
                    const Icon = meta.icon;
                    const isSystem = entry.user_role === 'system';
                    return (
                      <div key={idx} className="grid px-5 py-3 text-sm items-start" style={{ gridTemplateColumns: '170px 200px 180px 1fr' }} data-testid={`history-entry-${idx}`}>
                        <div style={{ color: '#52525B' }}>{formatWhen(entry.timestamp)}</div>
                        <div>
                          <p className="font-medium" style={{ color: '#101820' }}>{entry.user_name || '—'}</p>
                          <p className="text-xs" style={{ color: '#9a9488' }}>
                            {isSystem ? 'Automated' : entry.user_email}
                          </p>
                        </div>
                        <div className="flex items-center gap-2" style={{ color: meta.color }}>
                          <Icon weight="duotone" className="w-4 h-4 flex-shrink-0" />
                          <span className="text-sm font-medium">{meta.label}</span>
                        </div>
                        <div style={{ color: '#101820' }}>{entry.summary}</div>
                      </div>
                    );
                  })}
                </div>
              </motion.div>
            )}
          </>
        )}

        {tab === 'release' && (
          <div className="card-swiss">
            {releaseLoading ? (
              <p style={{ color: '#9a9488' }}>Loading release notes…</p>
            ) : !releaseNotes ? (
              <div className="flex flex-col items-center text-center py-8" style={{ color: '#9a9488' }}>
                <FileText weight="duotone" className="w-10 h-10 mb-2" />
                <p>No release notes yet. New updates will appear here as the app evolves.</p>
              </div>
            ) : (
              <div className="flex items-start justify-between gap-4 mb-4 flex-wrap">
                <div>
                  <p className="label-uppercase mb-1" style={{ color: '#1d57a5' }}>Product updates</p>
                  <h2 className="text-lg font-semibold" style={{ color: '#141b4d' }}>Release Notes</h2>
                </div>
                <button
                  onClick={fetchReleaseNotes}
                  className="text-sm font-medium inline-flex items-center gap-1 hover:underline"
                  style={{ color: '#1d57a5' }}
                  data-testid="refresh-release-notes"
                >
                  <ArrowCounterClockwise weight="bold" className="w-4 h-4" />
                  Refresh
                </button>
              </div>
            )}
            {releaseNotes && (
              <pre className="whitespace-pre-wrap text-sm leading-relaxed font-sans" style={{ color: '#101820' }}>
                {releaseNotes}
              </pre>
            )}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
